import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ListStateShell — one look for the two non-content list states (design 01-architecture §4.1):
 *   loading → a pulse skeleton (so the first paint is never a surface's empty copy — X-1)
 *   error   → an explicit error + a working retry (never the empty copy — "we can't see it" must
 *             never render as "there is none")
 *   ready   → children (each surface keeps its OWN populated/empty-by-truth rendering; empty is
 *             content, not chrome, and this shell must never absorb it)
 *
 * testIdBase gives each surface its addressable states: `${base}-skeleton`, `${base}-error`,
 * `${base}-retry` — a single shared testid would make every per-surface AC unfalsifiable about
 * WHICH surface failed.
 */
export const ListStateShell: React.FC<{
  status: 'loading' | 'ready' | 'error';
  testIdBase: string;
  onRetry: () => void;
  children: React.ReactNode;
}> = ({ status, testIdBase, onRetry, children }) => {
  const { t } = useTranslation('common');
  if (status === 'loading') {
    return (
      <div data-testid={`${testIdBase}-skeleton`} className="p-4 flex flex-col gap-3" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 rounded-xl bg-border-neutral/40 animate-pulse" />
        ))}
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div data-testid={`${testIdBase}-error`} className="p-6 flex flex-col items-center gap-3 text-center">
        <div className="text-sm text-text-muted">{t('listStates.error')}</div>
        <button type="button" data-testid={`${testIdBase}-retry`} onClick={onRetry}
          className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
          {t('listStates.retry')}
        </button>
      </div>
    );
  }
  return <>{children}</>;
};
